import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogTitle, DialogContent, Typography, Button, Stack, Box, Alert } from "@mui/material";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import { FieldTypeDateTime } from "../FieldTypeDateTime";
export type SchedulePublishDialogProps = {
  itemName: string;
  currentVersion: number;
  scheduledLocalText: string;
  creatorName: string;
  savedAgo: string;
  tzLabel: string;
  publishDateTime: string;
  publishTimezone: string;
  isLoading: boolean;
  isSelectedDatetimePast: boolean;
  isAlreadyScheduled: boolean;
  onClose: () => void;
  onPublishNow: () => void;
  onSchedule: () => void;
  onUnschedule: () => void;
  onDateTimeChange: (datetime: any) => void;
  onTimezoneChange: (timezone: any) => void;
};
export const SchedulePublishDialog = ({
  itemName,
  currentVersion,
  scheduledLocalText,
  creatorName,
  savedAgo,
  tzLabel,
  publishDateTime,
  publishTimezone,
  isLoading,
  isSelectedDatetimePast,
  isAlreadyScheduled,
  onClose,
  onPublishNow,
  onSchedule,
  onUnschedule,
  onDateTimeChange,
  onTimezoneChange
}: SchedulePublishDialogProps) => {
  const {
    t
  } = useTranslation();
  return <Dialog data-cy="SchedulePublishModal" open onClose={onClose} PaperProps={{
    sx: {
      maxWidth: 640,
      width: 640
    }
  }} data-sentry-element="Dialog" data-sentry-component="SchedulePublishDialog" data-sentry-source-file="SchedulePublishDialog.tsx">
      <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="SchedulePublishDialog.tsx">
        <Stack gap={1.5} data-sentry-element="Stack" data-sentry-source-file="SchedulePublishDialog.tsx">
          <Box sx={{
          backgroundColor: "warning.light",
          borderRadius: "100%",
          width: 40,
          height: 40,
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }} data-sentry-element="Box" data-sentry-source-file="SchedulePublishDialog.tsx">
            {isAlreadyScheduled ? <CalendarTodayRoundedIcon color="warning" /> : <ScheduleRoundedIcon color="warning" />}
          </Box>
          <Box data-sentry-element="Box" data-sentry-source-file="SchedulePublishDialog.tsx">
            <Box mb={1} data-sentry-element="Box" data-sentry-source-file="SchedulePublishDialog.tsx">
              <Typography variant="h5" display="inline" fontWeight={700} data-sentry-element="Typography" data-sentry-source-file="SchedulePublishDialog.tsx">
                {isAlreadyScheduled ? t("shell.schedulePublishTitleUnschedule") : t("shell.schedulePublishTitleSchedule")}
                {":"}
                &nbsp;
              </Typography>
              <Typography variant="h5" display="inline" data-sentry-element="Typography" data-sentry-source-file="SchedulePublishDialog.tsx">
                {itemName}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" data-sentry-element="Typography" data-sentry-source-file="SchedulePublishDialog.tsx">
              {isAlreadyScheduled ? t("shell.schedulePublishScheduledInfo", {
              version: currentVersion,
              date: scheduledLocalText,
              timezone: tzLabel
            }) : t("shell.schedulePublishSavedInfo", {
              version: currentVersion,
              distance: savedAgo,
              name: creatorName
            })}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent data-cy="PublishScheduleModal" data-sentry-element="DialogContent" data-sentry-source-file="SchedulePublishDialog.tsx">
        {isAlreadyScheduled ? <Alert severity="info" icon={<InfoRoundedIcon />}>
            {t("shell.schedulePublishUnscheduleInfo")}
          </Alert> : <>
            <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
              {t("shell.schedulePublishOnLabel")}
            </Typography>
            <FieldTypeDateTime disablePast showTimezonePicker showClearButton={false} name="publishDateTime" value={publishDateTime} selectedTimezone={publishTimezone} onChange={onDateTimeChange} onTimezoneChange={onTimezoneChange} />
            {isSelectedDatetimePast && <Alert severity="warning" icon={<WarningRoundedIcon fontSize="inherit" />} sx={{
          mt: 2.5
        }}>
                {t("shell.schedulePublishPastWarning")}
              </Alert>}
          </>}
      </DialogContent>

      <DialogActions data-sentry-element="DialogActions" data-sentry-source-file="SchedulePublishDialog.tsx">
        <Button data-cy="CancelSchedulePublishButton" variant="text" color="inherit" onClick={onClose} disabled={isLoading} data-sentry-element="Button" data-sentry-source-file="SchedulePublishDialog.tsx">
          {t("common.cancel")}
        </Button>

        {isAlreadyScheduled ? <Button data-cy="UnschedulePublishButton" variant="contained" color="warning" startIcon={<CalendarTodayRoundedIcon />} onClick={onUnschedule} loading={isLoading}>
            {t("shell.schedulePublishUnscheduleButton")}
          </Button> : <Button data-cy="SchedulePublishButton" variant="contained" startIcon={<ScheduleRoundedIcon />} onClick={isSelectedDatetimePast ? onPublishNow : onSchedule} loading={isLoading}>
            {t("shell.schedulePublishButton")}
          </Button>}
      </DialogActions>
    </Dialog>;
};